import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart3, FileText, Plus, CreditCard, DollarSign, Clock, Wrench,
  Users, User, Camera, Settings, Download, LogOut, Menu, Building, Mail, ScanSearch, Radio
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useLang } from '../../i18n';
import { Logo } from '../common/Logo';
import { InstallModal } from '../common/InstallModal';
import { LanguageSwitcher } from '../common/LanguageSwitcher';

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pwa = usePWAInstall();
  const { t } = useLang();

  const navItems = {
    customer: [
      { path: '/customer', label: t('admin_dashboard'), icon: BarChart3 },
      { path: '/customer/services', label: t('nav_services'), icon: DollarSign },
      { path: '/customer/requests', label: t('my_requests'), icon: FileText },
      { path: '/customer/new-request', label: t('new_request'), icon: Plus },
      { path: '/customer/invoices', label: t('my_invoices'), icon: CreditCard },
      { path: '/customer/blueprint', label: t('blueprint_builder'), icon: FileText }
    ],
    technician: [
      { path: '/technician', label: t('tech_dashboard'), icon: BarChart3 },
      { path: '/technician/jobs', label: t('tech_jobs'), icon: Wrench },
      { path: '/technician/time', label: t('admin_tracking'), icon: Clock }
    ],
    admin: [
      { path: '/admin', label: t('admin_dashboard'), icon: BarChart3 },
      { path: '/admin/requests', label: t('all_requests'), icon: FileText },
      { path: '/admin/tracking', label: t('admin_tracking'), icon: Clock },
      { path: '/admin/technicians', label: t('admin_technicians'), icon: Users },
      { path: '/admin/payroll', label: t('admin_payroll'), icon: DollarSign },
      { path: '/admin/customers', label: t('admin_customers'), icon: User },
      { path: '/admin/gallery', label: t('admin_gallery'), icon: Camera },
      { path: '/admin/site-content', label: t('admin_site_content'), icon: Settings },
      { path: '/admin/invoices', label: t('admin_invoices'), icon: CreditCard },
      { path: '/admin/emails', label: t('admin_emails'), icon: Mail },
      { path: '/admin/delivery', label: t('admin_delivery'), icon: Radio },
      { path: '/admin/blueprint-reader', label: t('bp_title'), icon: ScanSearch },
      { path: '/admin/floor-plan', label: t('admin_blueprint'), icon: FileText }
    ]
  };

  const currentNav = navItems[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 border-r border-gray-800 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-gray-800">
            <Logo />
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {currentNav.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? 'bg-red-600 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-800">
            {!pwa.isInstalled && (
              <button
                onClick={pwa.install}
                className="flex items-center gap-2 text-green-400 hover:text-green-300 text-sm w-full mb-3 px-2 py-2 bg-green-500/10 rounded-lg border border-green-500/20 hover:bg-green-500/20 transition-all"
                data-testid="sidebar-install-btn"
              >
                <Download className="w-4 h-4" />
                Install Free
              </button>
            )}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="text-white">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm w-full"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              {t('sign_in') === 'Sign In' ? 'Sign Out' : (t('sign_in') === 'Kyçu' ? 'Dil' : 'Cerrar Sesión')}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              className="lg:hidden p-2 hover:bg-gray-800 text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-400">
                <Building className="w-4 h-4" />
                BELLAJA Electrical & Security Systems
              </div>
              <LanguageSwitcher compact />
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <InstallModal pwa={pwa} />
    </div>
  );
};
